<template>
  <v-row no-gutters>
    <v-col cols="12" v-if="isFetching">
      <main-loader></main-loader>
    </v-col>
    <v-col cols="12" v-else>
      <draggable-lists
        v-bind="$attrs"
        v-on="listeners"
        :leftTitle="leftTitle"
        :rightTitle="rightTitle"
        :left-list="computedGroups"
        :right-list="searchedItems"
        :is-layers="true"
        @removedFromGroups="onRemovedFromGroups"
        @elementAdded="onElementAdded"
        @addGroup="openInputDialog($event, true, false)"
        @changeGroupName="openInputDialog($event, true, true)"
        @delete="deleteHandler"
        :disabled="disabled"
        :layersDotsItems="layersDotsItems"
      >
        <template #listElement="element">
          <draggable-lists-mobile-layers-item
            :key="element.id"
            v-bind="element"
            v-on="element.listeners"
            :disabled="disabled"
            :has-layer-dots-items="Boolean(layersDotsItems)"
            :cache-switcher="cacheSwitcher"
            :scope="scope"
            :schema_scope="schema_scope"
            @toggleHideGroup="toggleHideGroup(element)"
          >
            <template v-for="(value, key) in $scopedSlots" #[key]="scope">
              <slot :name="key" v-bind="scope" />
            </template>
          </draggable-lists-mobile-layers-item>
        </template>
        <template #rightListElement="element">
          <draggable-lists-mobile-layers-item
            :disabled="disabled"
            :key="element.id"
            :is-right="true"
            v-bind="element"
            :level="1"
          ></draggable-lists-mobile-layers-item>
        </template>
        <template #button>
          <v-row no-gutters align="center">
            <v-col>
              <data-input
                :data-type="{
                  name: 'select',
                }"
                dense
                hide-details
                outlined
                filled
                :value="selectedScope"
                :items="scopeItems"
                return-object
                item-text="text"
                item-value="value"
                @change="handleScopeChange"
                class="scope-select"
              ></data-input>
            </v-col>
            <v-col v-if="cacheSwitcher">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <span v-on="on">
                    <data-input
                      :data-type="{
                        name: 'switch',
                      }"
                      dense
                      hide-details
                      v-model="cacheFilter"
                    ></data-input>
                  </span>
                </template>
                {{ $i18n.t('admin.switchOnCacheLayers') }}
              </v-tooltip>
            </v-col>
          </v-row>
        </template>
        <template #search>
          <data-input
            :data-type="{ name: 'text' }"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            v-model="search"
            clearable
            height="32"
            hide-details
            :disabled="disabled"
            class="searchInput"
          >
          </data-input>
        </template>
      </draggable-lists>
    </v-col>

    <main-input-dialog
      dialog-width="40%"
      v-if="isInputDialogLoaded"
      :is-visible.sync="isInputDialogVisible"
      :title-translation="inputDialogTitle"
      :agree-text="inputDialogAgree"
      @agree="inputDialogAction"
      :inputs="addGroupInput"
      :loading="isAgreeButtonLoading"
    >
      <template #[`name.input`]="slot">
        <v-col>
          <v-row no-gutters>
            <v-col cols="12">
              <data-input v-model="name" v-bind="slot" v-on="$listeners" :rules="slot.rules"></data-input>
            </v-col>
          </v-row>
        </v-col>
      </template>
    </main-input-dialog>
    <main-dialog
      v-if="isDialogLoaded"
      :is-visible.sync="isDialogVisible"
      :title="$i18n.t(dialogTitle)"
      :body="$i18n.t(dialogBody, { name })"
      @agree="deleteLayerGroup"
      :loading="isConfirmButtonLoading"
      agree-text="dialog.agreeDelete"
    >
    </main-dialog>
  </v-row>
</template>

<script>
import DraggableLists from '@/components/DraggableLists';
import { call, get } from 'vuex-pathify';
import DraggableListsMobileLayersItem from '@/components/DraggableListsMobileLayersItem';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'DraggableListsMobileLayers',
  components: {
    MainLoader,
    DraggableListsMobileLayersItem,
    DraggableLists,
    DataInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    scope: {
      type: String,
      default: 'core',
    },
    scopeItems: {
      type: Array,
      default: () => [],
    },
    schema_scope: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    leftTitle: {
      type: String,
      default: 'admin.mobileLayers',
    },
    rightTitle: {
      type: String,
      default: 'admin.availableLayers',
    },
    allowedLayersTypes: {
      type: Array,
    },
    onlyGeometricLayers: {
      type: Boolean,
      default: false,
    },
    layersDotsItems: {
      type: Array,
    },
    initPromises: {
      type: Array,
      default: () => {
        return [];
      },
    },
    cacheSwitcher: {
      type: Boolean,
      default: false,
    },
  },
  data: vm => ({
    search: '',
    isFetching: false,
    selectedScope: vm.scope,
    name: undefined,
    isInputDialogLoaded: false,
    isInputDialogVisible: false,
    isAgreeButtonLoading: false,
    isConfirmButtonLoading: false,
    isDialogLoaded: false,
    isDialogVisible: false,
    isEdit: false,
    cacheFilter: false,
  }),
  computed: {
    layersGroupNameMaxLength: get('admin/settingsValues@layers_group_name_max_length'),
    layersAll: get('layers/layers'),
    cacheLayersMetadata: get('layers/cacheLayersMetadata'),
    searchedItems() {
      let items = this.sortItems();
      if (this.allowedLayersTypes) {
        items = items.filter(item => this.allowedLayersTypes.includes(item.type));
      }
      if (this.onlyGeometricLayers) {
        items = items.filter(item => {
          return this.layersAll[item.id]?.geometry_type || false;
        });
      }
      if (this.cacheSwitcher && this.cacheFilter) {
        items = items.filter(item => Object.keys(this.cacheLayersMetadata).find(key => key == item.id));
      }
      return this.search ? items.filter(layer => layer.name.toLowerCase().includes(this.search.toLowerCase())) : items;
    },
    layers: get('layers/layersSchemaByScope@:selectedScope.layers'),
    groups: get('layers/layersSchemaByScope@:scope.groups'),
    computedGroups() {
      const layersHiddenGroups = JSON.parse(localStorage.getItem('layers-hidden-groups') || '{}');
      return this.groups.map(group => {
        return {
          ...group,
          layers: group.layers.map(layer => {
            const cacheLayer = Object.values(this.cacheLayersMetadata).find(cLayer => cLayer.id == layer.id);
            return {
              ...layer,
              group_id: layer.group_id || group.id,
              hasCache: cacheLayer ? true : false,
              cacheDisabled: !cacheLayer?.last_cache_datetime ? true : false,
            };
          }),
          isHidden: !!layersHiddenGroups?.[group.id],
        };
      });
    },
    listeners() {
      const listeners = {
        ...this.$listeners,
      };
      if (this.layersDotsItems) {
        for (const dotsItem of this.layersDotsItems) {
          listeners[dotsItem.name] = e => {
            this.$emit(dotsItem.name, e);
          };
        }
      }
      return listeners;
    },
    addGroupInput() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.name',
          'validate-on-blur': !this.isEdit,
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.layersGroupNameMaxLength,
          },
          hint: this.$i18n.t('dialog.newGroupFieldHint'),
        },
      ];
    },
    dialogBody() {
      return `dialog.body.${this.isGroup ? 'deleteLayersGroup' : 'deleteFromSystem'}`;
    },
    dialogTitle() {
      return `dialog.title.${this.isGroup ? 'deleteGroup' : 'deleteFromSystem'}`;
    },
    inputDialogTitle() {
      return this.$i18n.t(this.isEdit ? 'dialog.title.manageLayer' : 'dialog.title.addDraggableGroup');
    },
    inputDialogAgree() {
      return this.isEdit ? 'button.save' : 'dialog.agreeAdd';
    },
    inputDialogAction() {
      if (this.isGroup) {
        return this.isEdit ? this.editGroup : this.addGroup;
      }
      return this.editLayer;
    },
  },
  methods: {
    attachMultipleLayerGroup: call('layers/attachMultipleLayerGroup'),
    editLayerGroup: call('layers/editLayerGroup'),
    deleteLayerGroupAction: call('layers/deleteLayerGroup'),
    addNewLayersGroup: call('layers/addNewLayersGroup'),
    getLayersSchemaByScope: call('layers/getLayersSchemaByScope'),
    resetData() {
      this.name = null;
      this.id = null;
    },
    async handleScopeChange(scope) {
      this.selectedScope = scope.value;
      await this.init();
    },
    async deleteLayerGroup() {
      await this.handleConfirmationAgree('deleteLayerGroupAction', {
        group_id: this.id,
        scope: this.scope,
        schema_scope: this.schema_scope,
      });
      this.resetData();
    },
    async handleConfirmationAgree(action, payload) {
      try {
        this.isConfirmButtonLoading = true;
        await this[action](payload);
      } catch (error) {
        console.log(error);
      }
      this.isConfirmButtonLoading = false;
      this.isDialogLoaded = false;
      this.isDialogVisible = false;
    },
    async editAddWrapper(action, payload) {
      try {
        this.isAgreeButtonLoading = true;
        await this[action](payload);
      } catch (error) {
        console.log(error);
      }
      setTimeout(() => {
        this.resetData();
        this.isAgreeButtonLoading = false;
        this.isInputDialogVisible = false;
      }, 500);
    },
    async addGroup() {
      await this.editAddWrapper('addNewLayersGroup', {
        schema_scope: this.schema_scope,
        name: this.name,
        layer_scope: this.scope,
      });
      this.name = undefined;
    },
    editGroup() {
      this.editAddWrapper('editLayerGroup', {
        group_id: this.id,
        body: {
          name: this.name,
        },
        scope: this.scope,
        schema_scope: this.schema_scope,
      });
    },
    setData(e) {
      const { id, name, type } = e;
      this.name = name;
      this.id = id;
      this.layerType = type + 's';
    },
    openInputDialog(e, isGroup, isEdit) {
      this.isGroup = isGroup;
      this.isEdit = isEdit;
      if (isEdit) {
        this.setData(e);
      }
      this.isInputDialogVisible = true;
      this.isInputDialogLoaded = true;
    },
    onRemovedFromGroups(e) {
      this.attachMultipleLayerGroup({
        body: {
          layers: e.map(x => {
            return { layer_id: x.element.id };
          }),
          group_id: null,
        },
        scope: this.scope,
        schema_scope: this.schema_scope,
      });
      if (this.selectedScope && this.selectedScope !== this.scope) {
        this.editAddWrapper('getLayersSchemaByScope', {
          schema_scope: this.schema_scope,
          layer_scope: this.selectedScope,
        });
      }
    },
    openConfirmationDialog(e) {
      this.setData(e);
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    onElementAdded(e) {
      const { toGroup, elementId, order_in_group, isGroup } = e;
      if (!isGroup) {
        this.attachMultipleLayerGroup({
          body: {
            layers: elementId,
            group_id: +toGroup,
            order_in_group: +order_in_group,
          },
          scope: this.scope,
          schema_scope: this.schema_scope,
        });
        if (this.selectedScope && this.selectedScope !== this.scope) {
          this.editAddWrapper('getLayersSchemaByScope', {
            schema_scope: this.schema_scope,
            layer_scope: this.selectedScope,
          });
        }
      } else {
        this.editAddWrapper('editLayerGroup', {
          group_id: elementId[0],
          body: {
            order_in_group,
          },
          scope: this.scope,
          schema_scope: this.schema_scope,
        });
      }
    },
    deleteHandler(e) {
      this.isGroup = true;
      this.openConfirmationDialog(e);
    },
    toggleHideGroup({ index, id, isHidden }) {
      this.$set(this.computedGroups[index], 'isHidden', !isHidden);
      this.$forceUpdate();
      const layersHiddenGroups = JSON.parse(localStorage.getItem('layers-hidden-groups') || '{}');
      localStorage.setItem(
        'layers-hidden-groups',
        JSON.stringify({ ...layersHiddenGroups, [id]: !isHidden ? true : undefined })
      );
    },
    async init() {
      this.isFetching = true;
      await Promise.all([
        this.getLayersSchemaByScope({ layer_scope: this.selectedScope, schema_scope: this.schema_scope }),
        ...this.initPromises,
      ]);
      this.isFetching = false;
    },
    sortItems() {
      return [...this.layers]?.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type.localeCompare(b.type);
        } else {
          return b.id - a.id;
        }
      });
    },
  },
  created() {
    this.init();
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
  .v-select {
    margin-top: 0;
  }
  .v-input__slot fieldset {
    border-color: rgba(0, 0, 0, 0.12);
  }
  .v-input__slot {
    max-height: 32px !important;
    min-height: 32px !important;
  }
  .v-select__slot {
    max-height: 32px !important;
    min-height: 32px !important;
  }

  .v-select__selection {
    max-width: 100%;
  }
  .v-select__selections {
    input {
      display: none !important;
    }
  }
  .v-list-item__title {
    text-align: left;
  }
}
.scope-select {
  border-radius: 4px;
  max-width: fit-content;
}
</style>
